import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { unfollowPosts } from '@/mocks/homeData';

export default function UnfollowFeed() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const hotRank = [...unfollowPosts].sort((a, b) => b.likes - a.likes).slice(0, 4);

  return (
    <section id="unfollow" className="w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
        <SectionHeading
          eyebrow="分区 04 · 脱粉现场"
          title="脱粉原因与粉丝回踩：当事人自己怎么说"
          desc="这里收集的是曾经投入过的人写下的说明。语气各不相同，但我们保留了他们的原始表达顺序，方便你自行判断。"
          actionLabel="讨论区规则"
          actionHref="#join"
        />

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr] lg:gap-10">
          <ul className="flex flex-col gap-4">
            {unfollowPosts.map((post) => {
              const isLiked = Boolean(liked[post.title]);
              return (
                <li
                  key={post.title}
                  className="rounded-lg border border-foreground-200/80 bg-background-100 p-4 transition-colors duration-200 hover:border-foreground-300 md:p-5"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 font-label text-sm font-medium text-background-50">
                      {post.initial}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-label text-[13px] text-foreground-800">
                          {post.author}
                        </span>
                        <span className="font-label text-[11px] text-foreground-500">
                          {post.time}
                        </span>
                        {post.hot ? (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 font-label text-[10px] text-primary-700">
                            热议
                          </span>
                        ) : null}
                      </div>

                      <h4 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 md:text-[17px]">
                        {post.title}
                      </h4>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                        {post.excerpt}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-foreground-200 px-2.5 py-1 font-label text-[11px] text-foreground-600"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>

                      <div className="mt-4 flex items-center gap-5 border-t border-foreground-200/70 pt-3">
                        <span className="flex items-center gap-1.5 font-label text-[12px] text-foreground-500">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-chat-3-line text-sm" />
                          </span>
                          {post.replies} 回复
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            setLiked((prev) => ({ ...prev, [post.title]: !prev[post.title] }))
                          }
                          className={
                            isLiked
                              ? 'flex items-center gap-1.5 whitespace-nowrap font-label text-[12px] text-primary-600'
                              : 'flex items-center gap-1.5 whitespace-nowrap font-label text-[12px] text-foreground-500 transition-colors duration-200 hover:text-primary-600'
                          }
                        >
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i
                              className={
                                isLiked ? 'ri-heart-fill text-sm' : 'ri-heart-line text-sm'
                              }
                            />
                          </span>
                          {isLiked ? post.likes + 1 : post.likes} 认同
                        </button>
                        <span className="flex items-center gap-1.5 font-label text-[12px] text-foreground-500">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-share-forward-line text-sm" />
                          </span>
                          转发
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-foreground-200/80 bg-background-100 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">
                本周高认同讨论
              </h3>
              <ul className="mt-4 flex flex-col divide-y divide-foreground-200/70">
                {hotRank.map((post, index) => (
                  <li key={post.title} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                    <span className="font-heading text-base font-bold text-accent-600">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] leading-snug text-foreground-800">{post.title}</p>
                      <span className="mt-1 block font-label text-[11px] text-foreground-500">
                        {post.likes} 人认同 · {post.replies} 回复
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-secondary-50 p-5">
              <h3 className="font-heading text-base font-bold text-secondary-900">讨论区提醒</h3>
              <ul className="mt-3 flex flex-col gap-2.5">
                {[
                  '只说公开讨论过的内容，不搬运私人信息',
                  '就事论事，不针对个人生活进行揣测',
                  '代号化表达，避免误伤同名的人',
                ].map((text) => (
                  <li key={text} className="flex gap-2 font-label text-[13px] text-secondary-800">
                    <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                      <i className="ri-checkbox-circle-line text-sm" />
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}